import request from "../../utils/request";
import { Result } from "../../models/general";

// Re-export enums (values)
export {
    ProfileStatus,
    FieldGovernanceStatus,
    ApprovalStatus,
    ProfileAuditAction,
} from "../../models/publicProfile";

// Re-export types (interfaces)
export type {
    PublicProfileView,
    ProfileUserSummary,
    ProfileCompanySummary,
    ProfileSkillView,
    ProfileAchievementView,
    ProfileSocialLinkView,
    PublicProfileDetailView,
    ProfilePersonaView,
    ProfileContactView,
    PublicProfileInput,
    ProfilePersonaInput,
    ProfileContactInput,
    ProfileSocialLinkInput,
    ProfileAchievementInput,
    // HR Admin Settings types
    CompanyProfileSettingsView,
    DepartmentProfileSettingsView,
    ProfileFieldGovernanceView,
    CompanyAnalyticsView,
    DailyAnalyticsView,
    TopProfileView,
    ProfileAnalyticsSummaryView,
    ProfileAuditLogView,
    ProfileDirectoryItemView,
    CompanyProfileSettingsInput,
    DepartmentProfileSettingsInput,
    ProfileFieldGovernanceInput,
    BulkProfileStatusInput,
    CompanyBrandingView,
    ProfileSettingsStatusView,
    PersonaSettingsView,
    PersonaSettingsInput,
} from "../../models/publicProfile";

import type {
    PublicProfileView,
    PublicProfileDetailView,
    ProfilePersonaView,
    ProfileContactView,
    ProfileSocialLinkView,
    ProfileAchievementView,
    PublicProfileInput,
    ProfilePersonaInput,
    ProfileContactInput,
    ProfileSocialLinkInput,
    ProfileAchievementInput,
    // HR Admin Settings types
    CompanyProfileSettingsView,
    DepartmentProfileSettingsView,
    ProfileFieldGovernanceView,
    CompanyAnalyticsView,
    ProfileAnalyticsSummaryView,
    ProfileAuditLogView,
    ProfileDirectoryItemView,
    CompanyProfileSettingsInput,
    DepartmentProfileSettingsInput,
    ProfileFieldGovernanceInput,
    ProfileSettingsStatusView,
    PersonaSettingsView,
    PersonaSettingsInput,
} from "../../models/publicProfile";

// ============================================================================
// Profile Endpoints
// ============================================================================

/**
 * Create a new public profile for the authenticated user.
 * @param input The profile creation input
 * @returns The created profile
 */
export async function createProfile(input: PublicProfileInput): Promise<Result<PublicProfileView>> {
    const response = await request.post<Result<PublicProfileView>>(
        "PublicProfile/CreateProfile",
        input
    );
    return response.data;
}

/**
 * Get a profile by its ID.
 * Requires authentication.
 * @param profileId The profile ID
 * @returns The profile view
 */
export async function getProfile(profileId: number): Promise<Result<PublicProfileView>> {
    const response = await request.get<Result<PublicProfileView>>(
        `PublicProfile/GetProfile/${profileId}`
    );
    return response.data;
}

/**
 * Get a public profile by its slug.
 * Anonymous access allowed - used for public profile viewing.
 * @param slug The profile slug
 * @returns The profile view
 */
export async function getPublicProfile(slug: string): Promise<Result<PublicProfileView>> {
    const response = await request.get<Result<PublicProfileView>>(
        `PublicProfile/p/${slug}`
    );
    return response.data;
}

/**
 * Get full public profile details by slug including user, company, skills, achievements, and social links.
 * Anonymous access allowed - used for rendering the full public profile page.
 * @param slug The profile slug
 * @returns The comprehensive profile detail view
 */
export async function getFullPublicProfile(slug: string): Promise<Result<PublicProfileDetailView>> {
    const response = await request.get<Result<PublicProfileDetailView>>(
        `PublicProfile/p/${slug}/full`
    );
    return response.data;
}

/**
 * Get all profiles owned by the authenticated user.
 * This is a convenience endpoint that doesn't require passing a userId.
 * @returns List of profile views owned by the current user
 */
export async function getMyProfiles(): Promise<Result<PublicProfileView[]>> {
    const response = await request.get<Result<PublicProfileView[]>>(
        "PublicProfile/GetMyProfiles"
    );
    return response.data;
}

/**
 * Get analytics summary for the authenticated user's own profiles.
 * Returns view counts, shares, QR scans, and contact submissions.
 * @returns List of analytics summaries for the user's profiles
 */
export async function getMyProfileAnalytics(): Promise<Result<ProfileAnalyticsSummaryView[]>> {
    const response = await request.get<Result<ProfileAnalyticsSummaryView[]>>(
        "PublicProfile/GetMyProfileAnalytics"
    );
    return response.data;
}

/**
 * Get the profile settings status for the current user.
 * Returns whether profiles are enabled, toggle is allowed, and approval required.
 * @returns The profile settings status
 */
export async function getProfileSettingsStatus(): Promise<Result<ProfileSettingsStatusView>> {
    const response = await request.get<Result<ProfileSettingsStatusView>>(
        "PublicProfile/GetProfileSettingsStatus"
    );
    return response.data;
}

/**
 * Get all profiles for a specific user.
 * HR/Admin can view profiles of users in their company.
 * @param userId The user ID
 * @returns List of profile views for the user
 */
export async function getUserProfiles(userId: string): Promise<Result<PublicProfileView[]>> {
    const response = await request.get<Result<PublicProfileView[]>>(
        `PublicProfile/GetUserProfiles/${userId}`
    );
    return response.data;
}

/**
 * Update an existing profile.
 * @param profileId The profile ID to update
 * @param input The profile update input
 * @returns The updated profile view
 */
export async function updateProfile(profileId: number, input: PublicProfileInput): Promise<Result<PublicProfileView>> {
    const response = await request.post<Result<PublicProfileView>>(
        `PublicProfile/UpdateProfile/${profileId}`,
        input
    );
    return response.data;
}

/**
 * Delete a profile.
 * @param profileId The profile ID to delete
 * @returns Success result
 */
export async function deleteProfile(profileId: number): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        `PublicProfile/DeleteProfile/${profileId}`
    );
    return response.data;
}

/**
 * Regenerate the slug for a profile.
 * Creates a new unique URL for the profile.
 * @param profileId The profile ID
 * @returns The updated profile with new slug
 */
export async function regenerateSlug(profileId: number): Promise<Result<PublicProfileView>> {
    const response = await request.post<Result<PublicProfileView>>(
        `PublicProfile/RegenerateSlug/${profileId}`
    );
    return response.data;
}

/**
 * Publish a profile to make it publicly accessible.
 * If the persona requires approval, the profile will be set to PendingApproval status.
 * @param profileId The profile ID to publish
 * @returns The updated profile view
 */
export async function publishProfile(profileId: number): Promise<Result<PublicProfileView>> {
    const response = await request.post<Result<PublicProfileView>>(
        `PublicProfile/PublishProfile/${profileId}`
    );
    return response.data;
}

/**
 * Unpublish a profile to remove it from public access.
 * The profile will be set to Draft status.
 * @param profileId The profile ID to unpublish
 * @returns The updated profile view
 */
export async function unpublishProfile(profileId: number): Promise<Result<PublicProfileView>> {
    const response = await request.post<Result<PublicProfileView>>(
        `PublicProfile/UnpublishProfile/${profileId}`
    );
    return response.data;
}

// ============================================================================
// Analytics Endpoints
// ============================================================================

/**
 * Record a profile view for analytics tracking.
 * Anonymous access allowed - called when a public profile is viewed.
 * @param slug The profile slug
 * @returns Success result
 */
export async function recordProfileView(slug: string): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        `PublicProfile/p/${slug}/view`
    );
    return response.data;
}

/**
 * Record a profile share for analytics tracking.
 * Anonymous access allowed - called when a public profile is shared.
 * @param slug The profile slug
 * @returns Success result
 */
export async function recordProfileShare(slug: string): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        `PublicProfile/p/${slug}/share`
    );
    return response.data;
}

// ============================================================================
// Contact Endpoints
// ============================================================================

/**
 * Get all contact submissions for a profile.
 * Only the profile owner or HR of the same company can view contacts.
 * @param profileId The profile ID
 * @returns List of contact submissions
 */
export async function getProfileContacts(profileId: number): Promise<Result<ProfileContactView[]>> {
    const response = await request.get<Result<ProfileContactView[]>>(
        `PublicProfile/GetProfileContacts/${profileId}`
    );
    return response.data;
}

/**
 * Submit contact information from a profile visitor.
 * Anonymous access allowed - used by visitors to share their contact details.
 * @param slug The profile slug
 * @param input The contact submission input (profileSlug is set automatically)
 * @returns Success result
 */
export async function submitContact(slug: string, input: Omit<ProfileContactInput, 'profileSlug'>): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        `PublicProfile/p/${slug}/contact`,
        { ...input, profileSlug: slug }
    );
    return response.data;
}

// ============================================================================
// Social Link Endpoints
// ============================================================================

/**
 * Add a social link to a profile.
 * @param input The social link input
 * @returns The created social link
 */
export async function addSocialLink(input: ProfileSocialLinkInput): Promise<Result<ProfileSocialLinkView>> {
    const response = await request.post<Result<ProfileSocialLinkView>>(
        "PublicProfile/AddSocialLink",
        input
    );
    return response.data;
}

/**
 * Get all social links for a profile.
 * @param profileId The profile ID
 * @returns List of social links
 */
export async function getSocialLinks(profileId: number): Promise<Result<ProfileSocialLinkView[]>> {
    const response = await request.get<Result<ProfileSocialLinkView[]>>(
        `PublicProfile/GetSocialLinks/${profileId}`
    );
    return response.data;
}

/**
 * Update an existing social link.
 * @param linkId The social link ID
 * @param input The social link update input
 * @returns The updated social link
 */
export async function updateSocialLink(linkId: number, input: ProfileSocialLinkInput): Promise<Result<ProfileSocialLinkView>> {
    const response = await request.post<Result<ProfileSocialLinkView>>(
        `PublicProfile/UpdateSocialLink/${linkId}`,
        input
    );
    return response.data;
}

/**
 * Delete a social link.
 * @param linkId The social link ID
 * @returns Success result
 */
export async function deleteSocialLink(linkId: number): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        `PublicProfile/DeleteSocialLink/${linkId}`
    );
    return response.data;
}

// ============================================================================
// Achievement Endpoints
// ============================================================================

/**
 * Add an achievement to a profile.
 * @param input The achievement input
 * @returns The created achievement
 */
export async function addAchievement(input: ProfileAchievementInput): Promise<Result<ProfileAchievementView>> {
    const response = await request.post<Result<ProfileAchievementView>>(
        "PublicProfile/AddAchievement",
        input
    );
    return response.data;
}

/**
 * Get all achievements for a profile.
 * @param profileId The profile ID
 * @returns List of achievements
 */
export async function getAchievements(profileId: number): Promise<Result<ProfileAchievementView[]>> {
    const response = await request.get<Result<ProfileAchievementView[]>>(
        `PublicProfile/GetAchievements/${profileId}`
    );
    return response.data;
}

/**
 * Update an existing achievement.
 * @param achievementId The achievement ID
 * @param input The achievement update input
 * @returns The updated achievement
 */
export async function updateAchievement(achievementId: number, input: ProfileAchievementInput): Promise<Result<ProfileAchievementView>> {
    const response = await request.post<Result<ProfileAchievementView>>(
        `PublicProfile/UpdateAchievement/${achievementId}`,
        input
    );
    return response.data;
}

/**
 * Delete an achievement.
 * @param achievementId The achievement ID
 * @returns Success result
 */
export async function deleteAchievement(achievementId: number): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        `PublicProfile/DeleteAchievement/${achievementId}`
    );
    return response.data;
}

// ============================================================================
// HR Approval Endpoints
// ============================================================================

/**
 * Get all profiles pending approval for the HR user's company.
 * HR role required.
 * @returns List of profiles with PendingApproval status
 */
export async function getPendingApprovalProfiles(): Promise<Result<PublicProfileView[]>> {
    const response = await request.get<Result<PublicProfileView[]>>(
        "PublicProfile/GetPendingApprovalProfiles"
    );
    return response.data;
}

/**
 * Approve a pending profile, setting it to Active and public.
 * HR role required.
 * @param profileId The profile ID to approve
 * @returns The updated profile view
 */
export async function approveProfile(profileId: number): Promise<Result<PublicProfileView>> {
    const response = await request.post<Result<PublicProfileView>>(
        `PublicProfile/ApproveProfile/${profileId}`
    );
    return response.data;
}

/**
 * Reject a pending profile, setting it back to Draft status.
 * HR role required.
 * @param profileId The profile ID to reject
 * @param reason Optional rejection reason
 * @returns The updated profile view
 */
export async function rejectProfile(profileId: number, reason?: string): Promise<Result<PublicProfileView>> {
    const response = await request.post<Result<PublicProfileView>>(
        `PublicProfile/RejectProfile/${profileId}`,
        { reason }
    );
    return response.data;
}

// ============================================================================
// Persona Endpoints (HR Only)
// ============================================================================

/**
 * Create a new persona for the company.
 * HR role required.
 * @param input The persona input
 * @returns The created persona
 */
export async function createPersona(input: ProfilePersonaInput): Promise<Result<ProfilePersonaView>> {
    const response = await request.post<Result<ProfilePersonaView>>(
        "PublicProfile/CreatePersona",
        input
    );
    return response.data;
}

/**
 * Get all personas for the current user's company.
 * @returns List of personas
 */
export async function getPersonas(): Promise<Result<ProfilePersonaView[]>> {
    const response = await request.get<Result<ProfilePersonaView[]>>(
        "PublicProfile/GetPersonas"
    );
    return response.data;
}

/**
 * Update an existing persona.
 * HR role required.
 * @param personaId The persona ID
 * @param input The persona update input
 * @returns The updated persona
 */
export async function updatePersona(personaId: number, input: ProfilePersonaInput): Promise<Result<ProfilePersonaView>> {
    const response = await request.post<Result<ProfilePersonaView>>(
        `PublicProfile/UpdatePersona/${personaId}`,
        input
    );
    return response.data;
}

/**
 * Delete a persona.
 * HR role required. Cannot delete personas with existing profiles.
 * @param personaId The persona ID
 * @returns Success result
 */
export async function deletePersona(personaId: number): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        `PublicProfile/DeletePersona/${personaId}`
    );
    return response.data;
}

// ============================================================================
// HR Admin Settings Endpoints
// ============================================================================

/**
 * Get company-wide public profile settings.
 * HR role required.
 * @returns The company profile settings
 */
export async function getCompanySettings(): Promise<Result<CompanyProfileSettingsView>> {
    const response = await request.get<Result<CompanyProfileSettingsView>>(
        "PublicProfile/GetCompanySettings"
    );
    return response.data;
}

/**
 * Update company-wide public profile settings.
 * HR role required.
 * @param input The settings input
 * @returns The updated settings
 */
export async function updateCompanySettings(input: CompanyProfileSettingsInput): Promise<Result<CompanyProfileSettingsView>> {
    const response = await request.post<Result<CompanyProfileSettingsView>>(
        "PublicProfile/UpdateCompanySettings",
        input
    );
    return response.data;
}

/**
 * Get profile settings for all departments.
 * HR role required.
 * @returns List of department settings
 */
export async function getDepartmentSettings(): Promise<Result<DepartmentProfileSettingsView[]>> {
    const response = await request.get<Result<DepartmentProfileSettingsView[]>>(
        "PublicProfile/GetDepartmentSettings"
    );
    return response.data;
}

/**
 * Update profile settings for a specific department.
 * HR role required.
 * @param departmentId The department ID
 * @param input The settings input
 * @returns The updated settings
 */
export async function updateDepartmentSetting(departmentId: number, input: DepartmentProfileSettingsInput): Promise<Result<DepartmentProfileSettingsView>> {
    const response = await request.post<Result<DepartmentProfileSettingsView>>(
        `PublicProfile/UpdateDepartmentSetting/${departmentId}`,
        input
    );
    return response.data;
}

/**
 * Get persona-specific settings merged with company defaults.
 * HR role required.
 * @param personaId The persona ID
 * @returns The merged persona settings
 */
export async function getPersonaSettings(personaId: number): Promise<Result<PersonaSettingsView>> {
    const response = await request.get<Result<PersonaSettingsView>>(
        `PublicProfile/GetPersonaSettings/${personaId}`
    );
    return response.data;
}

/**
 * Update persona-specific settings.
 * HR role required.
 * @param input The persona settings input
 * @returns The updated persona settings
 */
export async function updatePersonaSettings(input: PersonaSettingsInput): Promise<Result<PersonaSettingsView>> {
    const response = await request.post<Result<PersonaSettingsView>>(
        `PublicProfile/UpdatePersonaSettings`,
        input
    );
    return response.data;
}

/**
 * Get field governance rules for a persona.
 * HR role required.
 * @param personaId The persona ID
 * @returns List of field governance rules
 */
export async function getFieldGovernance(personaId: number): Promise<Result<ProfileFieldGovernanceView[]>> {
    const response = await request.get<Result<ProfileFieldGovernanceView[]>>(
        `PublicProfile/GetFieldGovernance/${personaId}`
    );
    return response.data;
}

/**
 * Update a single field governance rule.
 * HR role required.
 * @param personaId The persona ID
 * @param input The field governance input
 * @returns The updated field governance
 */
export async function updateFieldGovernance(personaId: number, input: ProfileFieldGovernanceInput): Promise<Result<ProfileFieldGovernanceView>> {
    const response = await request.post<Result<ProfileFieldGovernanceView>>(
        `PublicProfile/UpdateFieldGovernance/${personaId}`,
        input
    );
    return response.data;
}

/**
 * Bulk update field governance rules for a persona.
 * HR role required.
 * @param personaId The persona ID
 * @param inputs The list of field governance inputs
 * @returns The updated field governance list
 */
export async function bulkUpdateFieldGovernance(personaId: number, inputs: ProfileFieldGovernanceInput[]): Promise<Result<ProfileFieldGovernanceView[]>> {
    const response = await request.post<Result<ProfileFieldGovernanceView[]>>(
        `PublicProfile/BulkUpdateFieldGovernance/${personaId}`,
        inputs
    );
    return response.data;
}

/**
 * Get aggregated analytics for the company's public profiles.
 * HR role required.
 * @param startDate Optional start date for the date range
 * @param endDate Optional end date for the date range
 * @returns The company analytics
 */
export async function getCompanyAnalytics(startDate?: string, endDate?: string): Promise<Result<CompanyAnalyticsView>> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await request.get<Result<CompanyAnalyticsView>>(
        `PublicProfile/GetCompanyAnalytics?${params.toString()}`
    );
    return response.data;
}

/**
 * Get analytics summary for each profile in the company.
 * HR role required.
 * @param startDate Optional start date for the date range
 * @param endDate Optional end date for the date range
 * @returns List of profile analytics summaries
 */
export async function getProfileAnalyticsSummary(startDate?: string, endDate?: string): Promise<Result<ProfileAnalyticsSummaryView[]>> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await request.get<Result<ProfileAnalyticsSummaryView[]>>(
        `PublicProfile/GetProfileAnalyticsSummary?${params.toString()}`
    );
    return response.data;
}

/**
 * Get audit log entries for the company's public profiles.
 * HR role required.
 * @param limit Optional limit for pagination
 * @param offset Optional offset for pagination
 * @returns List of audit log entries
 */
export async function getAuditLog(limit?: number, offset?: number): Promise<Result<ProfileAuditLogView[]>> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());

    const response = await request.get<Result<ProfileAuditLogView[]>>(
        `PublicProfile/GetAuditLog?${params.toString()}`
    );
    return response.data;
}

/**
 * Get all profiles in the company with filtering options.
 * HR role required.
 * @param status Optional status filter
 * @param department Optional department ID filter
 * @param search Optional search query
 * @returns List of profile directory items
 */
export async function getProfileDirectory(status?: string, department?: string, search?: string): Promise<Result<ProfileDirectoryItemView[]>> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (department) params.append('department', department);
    if (search) params.append('search', search);

    const response = await request.get<Result<ProfileDirectoryItemView[]>>(
        `PublicProfile/GetProfileDirectory?${params.toString()}`
    );
    return response.data;
}

/**
 * Bulk update profile status (activate, deactivate, approve, reject).
 * HR role required.
 * @param profileIds The list of profile IDs to update
 * @param action The action to perform (activate, deactivate, approve, reject)
 * @returns Success result
 */
export async function bulkUpdateProfileStatus(profileIds: number[], action: string): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(
        "PublicProfile/BulkUpdateProfileStatus",
        { profileIds, action }
    );
    return response.data;
}
